import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Configuração da URL base da API
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

    // Configuração do Axios para incluir credenciais (cookies)
    axios.defaults.withCredentials = true;

    // Interceptor para incluir token automaticamente em todas as requisições para a API
    axios.interceptors.request.use(
        (config) => {
            // Só adiciona o token se a URL for da nossa API
            if (config.url && config.url.startsWith(API_BASE_URL)) {
                const token = localStorage.getItem('access_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Interceptor para resposta - lidar com tokens expirados
    axios.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    // Tenta refresh do token
                    const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`);
                    const newToken = refreshResponse.data.access_token;

                    localStorage.setItem('access_token', newToken);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;

                    return axios(originalRequest);
                } catch (refreshError) {
                    // Se refresh falhar, redireciona para login
                    setUser(null);
                    localStorage.removeItem('access_token');
                    navigate('/login');
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    // Função para fazer login
    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                usu_ds_email: email,
                usu_ds_hash_senha: password
            });
            setUser(response.data);
            localStorage.setItem('access_token', response.data.access_token);
            navigate('/dashboard/overview');
            return response.data;
        } catch (error) {
            console.error("Login failed:", error.response?.data || error.message);
            throw error;
        }
    };

    // Função para fazer registro
    const register = async (email, password, firstName, lastName) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/register`, {
                usu_ds_email: email,
                usu_ds_hash_senha: password,
                usu_nm_primeiro: firstName,
                usu_nm_ultimo: lastName
            });
            navigate('/login');
            return response.data;
        } catch (error) {
            console.error("Registration failed:", error.response?.data || error.message);
            throw error;
        }
    };

    // Função para fazer logout
    const logout = async () => {
        try {
            await axios.post(`${API_BASE_URL}/auth/logout`);
            setUser(null);
            localStorage.removeItem('access_token');
            navigate('/login');
        } catch (error) {
            console.error("Logout failed:", error.response?.data || error.message);
            throw error;
        }
    };

    // Função para verificar se o usuário está logado (verificando o access token)
    const checkAuthStatus = useCallback(async () => {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                const response = await axios.get(`${API_BASE_URL}/auth/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUser(response.data);
            } catch (error) {
                try {
                    const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`);
                    localStorage.setItem('access_token', refreshResponse.data.access_token);
                    const newProtectedResponse = await axios.get(`${API_BASE_URL}/auth/users/me`, {
                        headers: { Authorization: `Bearer ${refreshResponse.data.access_token}` }
                    });
                    setUser(newProtectedResponse.data);
                } catch (refreshError) {
                    console.error("Refresh token failed:", refreshError.response?.data || refreshError.message);
                    setUser(null);
                    localStorage.removeItem('access_token');
                    navigate('/login');
                }
            }
        }
        setLoading(false);
    }, [navigate, API_BASE_URL]);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    const value = {
        user,
        loading,
        login,
        register,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};